import React, { useState, useRef } from 'react';
import { Grid, Stack } from '@mui/material';
import {
    Text,
    Heading,
    Container,
    containerWidth,
    ChevronRightOutlineIcon,
    ChevronLeftOutlineIcon,
    colors,
} from '@web-for-marketing/react-ui';
import { capterraTestimonialStyles as classes } from './styleObjects/CapterraTestimonialStyles';
import { CapterraTestimonialSectionData } from '@models/section/capterraTestimonialSection';
import { CarouselDotTabs } from '@components/BlogSection/BlogComponents/Home/CarouselDotTabs';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, A11y } from 'swiper/modules';
import 'swiper/css';
import type { Swiper as SwiperType } from 'swiper';
import { CapterraTestimonialCard } from '@components/SectionComponents/CapterraTestimonialCard';

export interface CapterraTestimonialProps {
    sectionData: CapterraTestimonialSectionData;
    headerSection: boolean;
}

export function CapterraTestimonial({ sectionData, headerSection }: CapterraTestimonialProps): React.ReactElement {
    const [activeStep, setActiveStep] = useState(0);
    const swiperRef = useRef<SwiperType | null>(null);
    const testimonialListArray = [];

    if (sectionData.testimonials.length > 4) {
        for (let i = 0; i < sectionData.testimonials.length; i += 4) {
            const testimonialCards = sectionData.testimonials.slice(i, i + 4);
            testimonialListArray.push(testimonialCards);
        }
    }

    const maxSteps = testimonialListArray.length > 0 ? testimonialListArray.length : 1;

    if (activeStep > maxSteps - 1) {
        setActiveStep(activeStep - 1);
    }

    function handleStepChange(step: number): void {
        setActiveStep(step);
        swiperRef.current?.slideTo(step);
    }

    function handleSlideChange(swiper: SwiperType): void {
        setActiveStep(swiper.activeIndex);
    }

    return (
        <Container maxWidth={containerWidth.lg}>
            <div css={classes.headerContainer}>
                <Heading variant='h2' component={headerSection ? 'h1' : 'h2'} align='center' css={classes.title}>
                    {sectionData.title}
                </Heading>
                {sectionData.description ? (
                    <Text variant='body1' color='primary'>
                        {sectionData.description}
                    </Text>
                ) : null}
            </div>
            <Stack
                direction='row'
                justifyContent='center'
                alignItems='center'
                flexWrap='nowrap'
                css={{ margin: '2rem 0' }}
            >
                {maxSteps === 1 ? null : (
                    <button
                        css={classes.chevronIcon}
                        onClick={() => {
                            handleStepChange(activeStep - 1);
                        }}
                        disabled={activeStep === 0}
                        aria-label='go to previous slide'
                    >
                        <ChevronLeftOutlineIcon
                            color={activeStep === 0 ? colors.core.smoke : colors.core.innovation}
                            height='33'
                            width='33'
                        />
                    </button>
                )}
                <Swiper
                    modules={[Keyboard, A11y]}
                    onBeforeInit={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    initialSlide={activeStep}
                    onSlideChange={handleSlideChange}
                    keyboard={{
                        enabled: true,
                    }}
                    a11y={{}}
                    speed={600}
                    slidesPerView={1}
                    allowTouchMove={true}
                    css={classes.swipeViewContainer}
                >
                    {testimonialListArray.length === 0 ? (
                        <SwiperSlide>
                            <Grid
                                container
                                justifyContent='center'
                                spacing={4}
                                data-testid='CapterraTestimonialSection'
                                css={classes.testimonialSectionContainer}
                            >
                                {sectionData.testimonials.map((testimonial, index) => (
                                    <Grid
                                        container
                                        justifyContent='center'
                                        size={{ xs: 12, sm: 12, md: 6, lg: 3 }}
                                        key={`${JSON.stringify(testimonial)}-${index}`}
                                    >
                                        <CapterraTestimonialCard testimonial={testimonial} />
                                    </Grid>
                                ))}
                            </Grid>
                        </SwiperSlide>
                    ) : (
                        testimonialListArray.map((testimonialList, index) => (
                            <SwiperSlide key={index} data-testid={`CapterraTestimonialSection${index}`}>
                                <Grid
                                    container
                                    justifyContent='center'
                                    spacing={4}
                                    data-testid='CapterraTestimonialSection'
                                    css={classes.testimonialSectionContainer}
                                    key={`${JSON.stringify(testimonialList)}-${index}`}
                                >
                                    {testimonialList.map((testimonial, idx) => (
                                        <Grid
                                            container
                                            justifyContent='center'
                                            size={{ xs: 12, sm: 12, md: 6, lg: 3 }}
                                            key={`${JSON.stringify(testimonial)}-${idx}`}
                                        >
                                            <CapterraTestimonialCard
                                                testimonial={testimonial}
                                                isHidden={activeStep !== index}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </SwiperSlide>
                        ))
                    )}
                </Swiper>
                {maxSteps === 1 ? null : (
                    <button
                        css={classes.chevronIcon}
                        onClick={() => {
                            handleStepChange(activeStep + 1);
                        }}
                        disabled={activeStep === maxSteps - 1}
                        aria-label='go to next slide'
                    >
                        <ChevronRightOutlineIcon
                            color={activeStep === maxSteps - 1 ? colors.core.smoke : colors.core.innovation}
                            height='33'
                            width='33'
                        />
                    </button>
                )}
            </Stack>
            <div>
                <CarouselDotTabs maxDots={maxSteps} activeDot={activeStep} onChangeSlide={handleStepChange} />
            </div>
        </Container>
    );
}
